import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, DollarSign, Home, TrendingUp, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const LoansAndMortgages = () => {
  const loanTypes = [
    {
      icon: Home,
      title: "Home Mortgages",
      description: "Competitive rates for your dream home with flexible terms up to 30 years.",
      features: ["Fixed & Variable Rates", "Down Payment Assistance", "Pre-Approval in 24 Hours"]
    },
    {
      icon: DollarSign,
      title: "Personal Loans",
      description: "Quick access to funds for any purpose with no collateral required.",
      features: ["Up to $50,000", "Same-Day Approval", "Flexible Repayment Terms"]
    },
    {
      icon: TrendingUp,
      title: "Business Loans",
      description: "Fuel your business growth with tailored financing solutions.",
      features: ["Working Capital", "Equipment Financing", "SBA Loans"]
    },
    {
      icon: Shield,
      title: "Auto Loans",
      description: "Drive away in your new vehicle with our competitive auto financing.",
      features: ["New & Used Cars", "Refinancing Options", "Low Interest Rates"]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">Loans & Mortgages</h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              Flexible financing solutions to help you achieve your goals, whether it's buying a home, starting a business, or managing personal expenses
            </p>
            <Button asChild className="bg-white text-blue-600 hover:bg-blue-50">
              <Link to="/contact">
                Apply Now
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Loan Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {loanTypes.map((loan, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                    <loan.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{loan.title}</h3>
                  <p className="text-gray-600 mb-6">{loan.description}</p>
                  <ul className="space-y-3">
                    {loan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Choose Our Loans?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're committed to providing transparent, affordable lending options
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">3.5%</div>
              <p className="text-xl font-semibold mb-2">Starting APR</p>
              <p className="text-gray-600">Competitive rates for qualified borrowers</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">24hrs</div>
              <p className="text-xl font-semibold mb-2">Quick Approval</p>
              <p className="text-gray-600">Get pre-approved in as little as one day</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">$500K</div>
              <p className="text-xl font-semibold mb-2">Maximum Loan</p>
              <p className="text-gray-600">Flexible loan amounts for your needs</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-lg sm:text-xl text-blue-100 mb-8">
            Our loan specialists are here to help you find the perfect financing solution
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-blue-700">
              <Link to="/user/register">Open Account</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoansAndMortgages;
